// src/utils/defaultSeason.js
//
// Pure builders for the auto-default season created at team-wizard finish
// (features/auto-default-season/). Kept free of Firebase imports so the
// naming rule and payload shape stay unit-testable in isolation.

// Same sample text the manual Create Season form pre-loads (and flags
// prominently as an example needing the club's own legal review).
// Exported so CreateSeason and the auto season can never drift apart.
export const DEFAULT_WAIVER = `By registering, I acknowledge that participation in youth soccer activities involves inherent risks of injury. I consent on behalf of my child to participate in all team activities and authorize emergency medical treatment if necessary.

I release and hold harmless the team, club, coaches, and any affiliated organization from liability for any injury, loss, or damage arising from participation.

I confirm that all information provided is accurate and that my child is in good physical health and able to participate.`;

/**
 * Month → seasonal label key + year (D2).
 *   Mar–May spring · Jun–Aug summer · Sep–Nov fall · Dec–Feb winter.
 * December belongs to the INCOMING year's winter (Dec 2026 → Winter 2027).
 */
export function seasonAutoNameParts(now = new Date()) {
  const m = now.getMonth(); // 0–11
  const y = now.getFullYear();
  if (m >= 2 && m <= 4) return { key: 'spring', year: y };
  if (m >= 5 && m <= 7) return { key: 'summer', year: y };
  if (m >= 8 && m <= 10) return { key: 'fall', year: y };
  return { key: 'winter', year: m === 11 ? y + 1 : y };
}

/**
 * Season document payload, field-for-field the shape CreateSeason.jsx
 * writes for a free flat-fee season — every season reader was built
 * against that shape, so parity is the compatibility contract
 * (02-architecture §3). Dates are local-midnight Date objects per the
 * seasonDates convention. Registration stays open until season END
 * (+120d), not the manual form's +30d — an auto season's coach may
 * drift, and a link that dies at day 30 recreates the OPEN-badge /
 * CLOSED-page bug class (D3).
 *
 * 2026-07-24 revision (D10, founder-directed): status is DRAFT, not
 * open. A season is a parent-facing contract (fee, open door, "You're
 * in!" emails) and none of that goes live without the coach's eyes on
 * it — the checklist's step 2 becomes a one-tap review-and-open.
 *
 * D11 (same day, founder-directed): NO waiver ships on the auto season.
 * Scrimly does not author legal text — coaches add their own waiver (or
 * opt into a sample preset, with its examples-only disclaimer) via the
 * season page. Their legal call, not ours.
 *
 * createdAt/updatedAt are the caller's job (serverTimestamp lives with
 * the Firestore imports, not here).
 */
export function buildDefaultSeasonPayload({ teamId, uid, name, now = new Date() }) {
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const today   = new Date(y, m, d);            // local midnight
  const plus120 = new Date(y, m, d + 120);      // Date normalizes overflow

  return {
    teamId,
    coachId: uid,
    name,
    status: 'draft',   // D10: coach opens it with one tap after review
    autoCreated: true,

    // Core dates
    registrationOpen:  today,
    registrationClose: plus120,
    seasonStart:       today,
    seasonEnd:         plus120,
    maxPlayers:        null,
    waivers: [],       // D11: no Scrimly-authored waiver, ever
    waiverText: '',

    // Pricing — free flat fee
    registrationFee: 0,
    pricingMode: 'flat',
    divisionPricing: null,
    pricingTiers: null,

    earlyBirdEnabled: false,
    earlyBirdDiscount: null,
    earlyBirdDeadline: null,

    siblingDiscountEnabled: false,
    siblingDiscountAmount: null,
    returningDiscountEnabled: false,
    returningDiscountAmount: null,

    financialAidEnabled: false,

    paymentPlanEnabled: false,
    paymentPlanInstallments: null,
    paymentPlanFrequency: null,
    paymentPlanChargeDay: null,

    processingFeeMode: 'pass_to_parent',
    whatsIncluded: [],
    requiredDocuments: [],
  };
}
